import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import ClearIcon from "@mui/icons-material/Clear";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import type { InputFilter } from "../../../../core/utils/professions.specialites.utils";
import { getSelectedFilterSummary, normalizeSearchText } from "../../../../core/utils/professions.specialites.utils";

interface FiltersMultiAutocompleteProps {
	label: string;
	value: InputFilter[];
	options: InputFilter[];
	error?: boolean;
	onChange: (value: InputFilter[]) => void;
	reset: () => void;
}

export default function FiltersMultiAutocomplete({ label, value, options, error = false, onChange, reset }: FiltersMultiAutocompleteProps) {
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
	const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));
	const containerRef = useRef<HTMLDivElement | null>(null);
	const [open, setOpen] = useState(false);
	const [inputValue, setInputValue] = useState("");
	const disabled = useMemo(() => options.length === 0, [options]);

	useEffect(() => {
		const clearSearch = () => {
			setInputValue("");
			setOpen(false);
		};
		window.addEventListener("teleexpertise-preferences-saved", clearSearch);
		return () => window.removeEventListener("teleexpertise-preferences-saved", clearSearch);
	}, []);

	// Recherche insensible à la casse, aux accents et aux séparateurs.
	const filterOptions = useCallback((currentOptions: InputFilter[], { inputValue }: { inputValue: string }) => {
		const normalizedInput = normalizeSearchText(inputValue);
		if (!normalizedInput) return currentOptions;
		return currentOptions.filter((option) => normalizeSearchText(option.label).includes(normalizedInput));
	}, []);

	const handleFocus = useCallback(() => {
		if (isSmallScreen) {
			requestAnimationFrame(() => containerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
		}
		if (!disabled) setOpen(true);
	}, [disabled, isSmallScreen]);

	const handleClear = useCallback(() => {
		// Efface la recherche et délègue la remise à zéro de la sélection au composant parent.
		setInputValue("");
		reset();
	}, [reset]);

	const hasValue = value.length > 0;
	const selectedKeys = useMemo(() => new Set(value.map((option) => option.optionKey)), [value]);
	const allSelected = options.length > 0 && options.every((option) => selectedKeys.has(option.optionKey));
	const maximumSummaryCharacters = isSmallScreen ? 28 : isMediumScreen ? 32 : 84;
	const selectionSummary = useMemo(() => getSelectedFilterSummary(options, value, maximumSummaryCharacters), [maximumSummaryCharacters, options, value]);

	const toggleAll = useCallback(() => {
		// Le bandeau du menu permet d'autoriser ou de retirer toutes les options en une action.
		if (allSelected) reset();
		else onChange(options);
	}, [allSelected, onChange, options, reset]);
	const paperStateRef = useRef({ allSelected, hasValue, toggleAll });
	paperStateRef.current = { allSelected, hasValue, toggleAll };

	const removeSummaryItem = useCallback(
		(optionKeys: string[]) => {
			// Une chip représente une profession complète ou une spécialité : retire toutes les options associées.
			const keysToRemove = new Set(optionKeys);
			onChange(value.filter((option) => !keysToRemove.has(option.optionKey)));
		},
		[onChange, value]
	);

	const PaperComponent = useCallback((props: React.HTMLAttributes<HTMLElement>) => {
		const { allSelected, hasValue, toggleAll } = paperStateRef.current;
		return (
			<Paper {...props}>
				{/* Ajoute l'action globale au début de la liste déroulante. */}
				<Box
					onClick={toggleAll}
					onMouseDown={(event) => event.preventDefault()}
					sx={{ display: "flex", alignItems: "center", cursor: "pointer", minHeight: 44, paddingLeft: 1.5 }}
				>
					<Checkbox checked={allSelected} indeterminate={hasValue && !allSelected} size="small" />
					<Typography variant="body2" fontSize="0.8125rem">
						{allSelected ? "Tout désélectionner" : "Tout sélectionner"}
					</Typography>
				</Box>
				<Divider />
				{props.children}
			</Paper>
		);
	}, []);

	return (
		<div ref={containerRef}>
			<Autocomplete
				multiple
				disableCloseOnSelect
				disableClearable
				forcePopupIcon={false}
				open={open}
				onFocus={handleFocus}
				onOpen={() => !disabled && setOpen(true)}
				onClose={() => setOpen(false)}
				onKeyDown={(event) => {
					// La sélection est résumée en chips : Backspace/Delete retire la dernière unité métier.
					if ((event.key === "Backspace" || event.key === "Delete") && inputValue === "" && hasValue) {
						event.defaultMuiPrevented = true;
						event.preventDefault();
						const lastItem = selectionSummary.allItems.at(-1);
						if (lastItem) removeSummaryItem(lastItem.optionKeys);
					}
				}}
				options={options}
				value={value}
				inputValue={inputValue}
				onInputChange={(_, nextInputValue, reason) => {
					if (reason === "input" || reason === "clear") setInputValue(nextInputValue);
				}}
				onChange={(_, newValue, reason) => {
					if (reason === "clear") {
						setInputValue("");
						reset();
					} else onChange(newValue);
				}}
				filterOptions={filterOptions}
				PaperComponent={PaperComponent}
				getOptionLabel={(option) => option.label}
				getOptionKey={(option) => option.optionKey}
				isOptionEqualToValue={(option, currentValue) => option.optionKey === currentValue.optionKey}
				disabled={disabled}
				size="small"
				fullWidth
				componentsProps={isSmallScreen ? { popper: { sx: { maxWidth: "calc(100vw - 32px)", width: "calc(100vw - 32px) !important" } } } : undefined}
				renderOption={(props, option) => {
					const { key, ...optionProps } = props as React.HTMLAttributes<HTMLLIElement> & { key: React.Key };
					const selected = selectedKeys.has(option.optionKey);
					const SelectionIcon = selected ? CheckBoxIcon : CheckBoxOutlineBlankIcon;
					const isSpecialite = option.filterType === "specialite";
					return (
						<Box
							component="li"
							key={key}
							{...optionProps}
							style={{
								...optionProps.style,
								paddingLeft: isSpecialite ? 32 : optionProps.style?.paddingLeft,
							}}
							sx={{
								backgroundColor: selected ? "#00796b2b" : undefined,
							}}
						>
							<SelectionIcon fontSize="small" style={{ marginRight: 8 }} />
							<Typography variant="body2" fontSize="0.8125rem" fontWeight={isSpecialite ? 400 : 700} color={isSpecialite ? "text.secondary" : undefined}>
								{option.label}
							</Typography>
						</Box>
					);
				}}
				renderTags={() => {
					if (!hasValue) return null;

					// N'affiche que les éléments tenant dans le champ ; les autres sont regroupés dans la chip "+ x".
					return (
						<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0, overflow: "hidden" }}>
							{selectionSummary.items.map((item) => (
								<Chip
									key={item.optionKeys.join(",")}
									label={item.label}
									onDelete={() => removeSummaryItem(item.optionKeys)}
									sx={{ flexShrink: 1, "& .MuiChip-label": { whiteSpace: "nowrap" } }}
								/>
							))}
							{selectionSummary.hiddenCount > 0 && <Chip label={`+${selectionSummary.hiddenCount}`} sx={{ flexShrink: 0 }} />}
						</Box>
					);
				}}
				renderInput={(params) => (
					<TextField
						{...params}
						label={label}
						error={error}
						inputProps={{
							...params.inputProps,
							autoComplete: "off",
						}}
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<InputAdornment position="end">
									{hasValue && (
										<Tooltip title="Tout désélectionner">
											<IconButton aria-label="Tout désélectionner" size="small" onClick={handleClear} onMouseDown={(event) => event.preventDefault()}>
												<ClearIcon fontSize="small" />
											</IconButton>
										</Tooltip>
									)}
									<Tooltip title={open ? "Fermer" : "Ouvrir"}>
										<IconButton
											aria-label={open ? "Fermer" : "Ouvrir"}
											size="small"
											onClick={() => !disabled && setOpen((current) => !current)}
											onMouseDown={(event) => event.preventDefault()}
										>
											<ArrowDropDownIcon fontSize="small" sx={{ transform: open ? "rotate(180deg)" : undefined }} />
										</IconButton>
									</Tooltip>
								</InputAdornment>
							),
						}}
					/>
				)}
			/>
		</div>
	);
}
